import { useEffect, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useIntuneQuery, intuneKeys } from '../hooks/useIntuneQuery'
import {
  listAzureADGroups,
  listAssignmentFilters,
  listAssignments,
  replaceAssignments,
  type AssignmentCreate,
} from '../services/api'
import { Modal } from './ui'
import AssignmentBlade, { toAssignmentGroup } from './AssignmentBlade'
import type { AssignmentRow, AssignmentGroup, Intent } from './AssignmentBlade'
import { rowsToAssignments, hasIncludeTarget, assignmentsToRows } from '../utils/assignmentPayload'
import { errorText } from '../utils/errorText'

/**
 * Editing who a LIVE deployment reaches.
 *
 * The same blade the deployment template authors, pointed at one rollout. The template is not
 * touched: this app was published against the settings it carries, and other apps on that template
 * keep theirs.
 *
 * Two blades, because one publish produces TWO Intune app objects — the install app and the
 * update-only twin — each with its own assignment set. They are edited side by side rather than
 * behind tabs: the twin patches devices that already have the app, so the pair is read together,
 * and a tab hid the fact that assigning one assigns nothing on the other.
 */

interface AssignmentModalProps {
  isOpen: boolean
  onClose: () => void
  /** The install app's Intune app id — the object this modal's first blade edits. */
  deploymentId: string
  appName: string
  onSuccess?: () => void
  /** The update-only twin's Intune app id, when the publish produced one. */
  updateOnlyAppId?: string
  /**
   * Which app object is being edited. They are two Intune apps with two populations, so a screen
   * that shows one must not quietly save the other.
   */
  scope?: 'app' | 'updateOnly' | 'both'
  /** The intents the app blade offers. An update-only app takes fewer than a full app. */
  appIntents?: Intent[]
}

/** Same set, same order? Rows are only ever replaced wholesale, so identity is enough. */
const same = (a: AssignmentRow[], b: AssignmentRow[]) => JSON.stringify(a) === JSON.stringify(b)

export default function AssignmentModal({
  isOpen,
  onClose,
  deploymentId,
  appName,
  onSuccess,
  updateOnlyAppId,
  scope = 'both',
  appIntents = ['required', 'available', 'uninstall'],
}: AssignmentModalProps) {
  const queryClient = useQueryClient()

  const showApp = scope !== 'updateOnly'
  const showUpdate = scope !== 'app' && !!updateOnlyAppId

  // ONE editor for an assignment, wherever it is edited. This modal used to carry its own cut-down
  // form — a single intent and a single settings block applied to every target — while the
  // deployment-template wizard used AssignmentBlade and could set notifications, availability,
  // deadline, restart grace, filters and delivery optimization per row. The API accepted all of it
  // either way (api/deployments.py, AssignmentSettings); only this screen could not express it.
  const [rows, setRows] = useState<AssignmentRow[]>([])
  const [updateRows, setUpdateRows] = useState<AssignmentRow[]>([])
  /** What Intune held when the modal opened, so only a blade that was CHANGED is written back. */
  const [initialRows, setInitialRows] = useState<AssignmentRow[]>([])
  const [initialUpdateRows, setInitialUpdateRows] = useState<AssignmentRow[]>([])
  const [validationError, setValidationError] = useState<string | null>(null)

  const [groupSearch, setGroupSearch] = useState('')
  const { data: groupsData } = useIntuneQuery({
    queryKey: intuneKeys.adGroups(groupSearch),
    queryFn: () => listAzureADGroups(groupSearch || undefined),
    enabled: isOpen,
    retry: false,
  })
  const bladeGroups: AssignmentGroup[] = (groupsData?.groups ?? []).map(toAssignmentGroup)

  // The blade's filter picker needs a source; without one it hides itself and the filter stays
  // unset forever. Non-fatal by design — [] just hides the control.
  const filtersQ = useIntuneQuery({
    queryKey: intuneKeys.assignmentFilters(),
    queryFn: listAssignmentFilters,
    enabled: isOpen,
    retry: false,
  })

  const appQ = useIntuneQuery({
    queryKey: intuneKeys.assignments(deploymentId),
    queryFn: () => listAssignments(deploymentId),
    enabled: isOpen && showApp && !!deploymentId,
    retry: false,
  })
  const updateQ = useIntuneQuery({
    queryKey: intuneKeys.assignments(updateOnlyAppId),
    queryFn: () => listAssignments(updateOnlyAppId!),
    enabled: isOpen && showUpdate,
    retry: false,
  })

  /**
   * Seed each blade with what its app object is ACTUALLY assigned right now.
   *
   * This screen edits the complete assignment set — saving replaces it (spec §7a.2) — so opening on
   * an empty blade showed the administrator none of what they were about to replace, and let them
   * only ever append. Keyed on the fetched data rather than on `isOpen`: the query resolves after
   * the modal opens, and seeding once on open would leave the blade empty.
   */
  useEffect(() => {
    const live = appQ.data?.assignments
    if (!isOpen || !live) return
    const seeded = assignmentsToRows(live)
    setRows(seeded)
    setInitialRows(seeded)
  }, [isOpen, appQ.data])

  useEffect(() => {
    const live = updateQ.data?.assignments
    if (!isOpen || !live) return
    const seeded = assignmentsToRows(live)
    setUpdateRows(seeded)
    setInitialUpdateRows(seeded)
  }, [isOpen, updateQ.data])

  useEffect(() => {
    if (!isOpen) return
    setGroupSearch('')
    setValidationError(null)
  }, [isOpen])

  const appDirty = showApp && !same(rows, initialRows)
  const updateDirty = showUpdate && !same(updateRows, initialUpdateRows)

  /**
   * Returns the payload for a blade, or an error to show instead.
   *
   * Graph accepts a target with a null groupId and then assigns the app to nobody, and an
   * exclusion with nothing to exclude from reaches no device either, so both are refused here
   * rather than sent and silently ignored.
   */
  function mapped(what: string, bladeRows: AssignmentRow[]): AssignmentCreate[] | string {
    const { assignments, skipped } = rowsToAssignments(bladeRows)
    if (skipped > 0) {
      return `${what}: ${skipped} assignment${skipped === 1 ? '' : 's'} target a group that has not been chosen yet.`
    }
    if (assignments.length === 0) return `${what}: add at least one included assignment.`
    if (!hasIncludeTarget(assignments)) {
      return `${what}: exclusion-only assignments have no effect without an include target.`
    }
    return assignments
  }

  /**
   * Saving REPLACES the app's assignment set — the blade holds every current assignment, so
   * sending it to the append endpoint would duplicate each one and could never remove a row the
   * operator deleted. Only a blade that was actually edited is written: the two objects are saved
   * independently, and touching the twin's set because its app's set changed would retarget a
   * population nobody asked to retarget.
   */
  const save = useMutation({
    mutationFn: async () => {
      if (appDirty) {
        const payload = mapped('The app', rows)
        if (typeof payload === 'string') throw new Error(payload)
        await replaceAssignments(deploymentId, payload)
      }
      if (updateDirty) {
        const payload = mapped('Update-only app', updateRows)
        if (typeof payload === 'string') throw new Error(payload)
        await replaceAssignments(updateOnlyAppId!, payload)
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: intuneKeys.assignments(deploymentId) })
      if (updateOnlyAppId) {
        queryClient.invalidateQueries({ queryKey: intuneKeys.assignments(updateOnlyAppId) })
      }
      onSuccess?.()
      onClose()
    },
  })

  const handleSave = () => {
    setValidationError(null)
    // Both mappings run before either request, so a bad update-only blade cannot leave the app's
    // assignments already replaced.
    for (const [what, bladeRows, dirty] of [
      ['The app', rows, appDirty],
      ['Update-only app', updateRows, updateDirty],
    ] as const) {
      if (!dirty) continue
      const payload = mapped(what, bladeRows)
      if (typeof payload === 'string') {
        setValidationError(payload)
        return
      }
    }
    save.mutate()
  }

  if (!isOpen) return null

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      size="xl"
      title={
        scope === 'updateOnly'
          ? `Update-only assignment — ${appName}`
          : `Assignment — ${appName}`
      }
      footer={
        <>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-theme-border-primary px-3 py-1.5 text-sm text-theme-text-primary hover:bg-theme-tertiary"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={save.isPending || (!appDirty && !updateDirty)}
            className="rounded-lg bg-green-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-green-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {save.isPending ? 'Saving…' : 'Save assignment'}
          </button>
        </>
      }
    >
      <p className="mb-4 rounded-lg border border-theme-border-primary bg-theme-tertiary px-4 py-2.5 text-[12px] text-theme-text-secondary">
        Applies to this deployment only. The template it came from is unchanged, and every other app
        using that template keeps its current targeting. Kept when this deployment gets a new
        version, automatically or with Update, and when you deploy it again with the same
        deployment template. Choosing another deployment template, or discarding edits, applies
        the template's values.
      </p>

      {showApp && (
        <>
          {showUpdate && (
            <p className="mb-2 text-sm font-medium text-theme-text-primary">The app</p>
          )}
          <AssignmentBlade
            value={rows}
            onChange={setRows}
            intents={appIntents}
            groups={bladeGroups}
            filters={filtersQ.data?.filters ?? []}
          />
        </>
      )}

      {showUpdate && (
        <>
          {scope !== 'updateOnly' && (
            <p className="mb-1 mt-6 text-sm font-medium text-theme-text-primary">Update-only app</p>
          )}
          <p className="mb-2 text-[12px] text-theme-text-tertiary">
            Patches devices that already have the app and never introduces it, so a broad target is
            safe here.
          </p>
          {/* Required only: nobody browses the Company Portal for an updater. */}
          <AssignmentBlade
            value={updateRows}
            onChange={setUpdateRows}
            intents={['required']}
            groups={bladeGroups}
            filters={filtersQ.data?.filters ?? []}
          />
        </>
      )}

      {validationError && (
        <div className="mt-3 rounded-lg border border-yellow-200 bg-yellow-50 p-3 dark:border-yellow-700 dark:bg-yellow-900/20">
          <p className="text-sm text-yellow-700 dark:text-yellow-400">{validationError}</p>
        </div>
      )}
      {save.error && (
        <div className="mt-3 rounded-lg border border-red-200 bg-red-50 p-3 dark:border-red-700 dark:bg-red-900/20">
          <p className="text-sm text-red-700 dark:text-red-400">{errorText(save.error, 'Saving the assignment failed.')}</p>
        </div>
      )}
    </Modal>
  )
}
